import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/EditOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/BlockOutlined";
import {
  useGetAdminMessageTemplatesQuery,
  useUpdateAdminMessageTemplateMutation,
  useActivateAdminMessageTemplateMutation,
  useDeactivateAdminMessageTemplateMutation,
  useResetAdminMessageTemplateMutation,
} from "../../../Api/attoDeskApi";

/**
 * Settings → Message Templates (Attendance Messaging Phase 2).
 *
 * Admin-only surface for the 5 tenant/global attendance quick-message
 * templates rendered as chips on the /class-sessions/:id/attendance
 * composer. Server enforces System_Admin on every write — this page
 * additionally self-gates on the route in Drawer/Routes.tsx.
 */

interface TplRow {
  id: number;
  key: string;
  name: string;
  subject_template: string;
  body_template: string;
  active: boolean;
  context: string;
  sort_order: number;
  has_default: boolean;
  updated_at?: string | null;
}

const MessageTemplatesPage: React.FC = () => {
  const q = useGetAdminMessageTemplatesQuery();
  const [updateTpl, updateState] = useUpdateAdminMessageTemplateMutation();
  const [activateTpl] = useActivateAdminMessageTemplateMutation();
  const [deactivateTpl] = useDeactivateAdminMessageTemplateMutation();
  const [resetTpl, resetState] = useResetAdminMessageTemplateMutation();

  const rows: TplRow[] = ((q.data?.data as any)?.rows) || [];
  const counts = ((q.data?.data as any)?.counts) || { total: 0, active: 0, inactive: 0 };
  const placeholders: string[] = ((q.data?.data as any)?.placeholders) || [];

  const [editing, setEditing] = useState<TplRow | null>(null);
  const [resetting, setResetting] = useState<TplRow | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" | "info" } | null>(null);

  const [form, setForm] = useState<{ name: string; subject_template: string; body_template: string; sort_order: number }>({
    name: "", subject_template: "", body_template: "", sort_order: 0,
  });

  const openEdit = (row: TplRow) => {
    setEditing(row);
    setForm({
      name: row.name,
      subject_template: row.subject_template,
      body_template: row.body_template,
      sort_order: row.sort_order,
    });
  };

  const insertToken = (token: string, field: "subject_template" | "body_template") => {
    setForm((f) => ({ ...f, [field]: (f[field] || "") + `{{${token}}}` }));
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      await updateTpl({ id: editing.id, ...form }).unwrap();
      setToast({ msg: "Template saved.", severity: "success" });
      setEditing(null);
    } catch (e: any) {
      setToast({ msg: e?.data?.message || "Failed to save template.", severity: "error" });
    }
  };

  const toggleActive = async (row: TplRow) => {
    try {
      const mut = row.active ? deactivateTpl : activateTpl;
      await mut({ id: row.id }).unwrap();
      setToast({
        msg: row.active ? "Template deactivated." : "Template activated.",
        severity: "success",
      });
    } catch (e: any) {
      setToast({ msg: e?.data?.message || "Failed to update status.", severity: "error" });
    }
  };

  const confirmReset = async () => {
    if (!resetting) return;
    try {
      await resetTpl({ id: resetting.id }).unwrap();
      setToast({ msg: "Template reset to Step Up default.", severity: "success" });
      setResetting(null);
    } catch (e: any) {
      setToast({ msg: e?.data?.message || "Failed to reset template.", severity: "error" });
    }
  };

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (a.sort_order - b.sort_order) || a.id - b.id),
    [rows]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1b3a6b" }}>
            Message Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Attendance session composer templates. Edits go live immediately for every staff member.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip label={`Total: ${counts.total}`} />
          <Chip color="success" label={`Active: ${counts.active}`} />
          <Chip color="default" variant="outlined" label={`Inactive: ${counts.inactive}`} />
        </Stack>
      </Stack>

      {q.isLoading && <LinearProgress sx={{ mb: 2 }} />}
      {q.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load templates.{" "}
          <Button size="small" onClick={() => q.refetch()}>Retry</Button>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Sort</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{r.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.key || "(custom)"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="body2" noWrap>{r.subject_template}</Typography>
                </TableCell>
                <TableCell>
                  {r.active ? (
                    <Chip icon={<CheckCircleIcon />} label="Active" color="success" size="small" />
                  ) : (
                    <Chip icon={<BlockIcon />} label="Inactive" size="small" />
                  )}
                </TableCell>
                <TableCell>{r.sort_order}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={r.active ? "Deactivate" : "Activate"}>
                    <IconButton size="small" onClick={() => toggleActive(r)}>
                      {r.active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={r.has_default ? "Reset to Step Up default" : "No default to reset from"}>
                    <span>
                      <IconButton size="small" disabled={!r.has_default} onClick={() => setResetting(r)}>
                        <RestartAltIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && !q.isLoading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                    No templates yet. Seed the AttendanceMessageTemplatesSeeder.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* EDIT DIALOG */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit template — {editing?.name}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value.slice(0, 120) })}
              fullWidth
              helperText={`${form.name.length} / 120`}
            />
            <TextField
              label="Subject"
              value={form.subject_template}
              onChange={(e) => setForm({ ...form, subject_template: e.target.value.slice(0, 191) })}
              fullWidth
              helperText={`${form.subject_template.length} / 191`}
            />
            <TextField
              label="Body"
              value={form.body_template}
              onChange={(e) => setForm({ ...form, body_template: e.target.value.slice(0, 5000) })}
              fullWidth
              multiline
              minRows={8}
              helperText={`${form.body_template.length} / 5000`}
            />
            <TextField
              label="Sort order"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
              sx={{ maxWidth: 180 }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Insert into body — supported placeholders
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                {placeholders.map((p) => (
                  <Chip
                    key={p}
                    label={`{{${p}}}`}
                    size="small"
                    clickable
                    onClick={() => insertToken(p, "body_template")}
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitEdit} disabled={updateState.isLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* RESET CONFIRM */}
      <Dialog open={!!resetting} onClose={() => setResetting(null)}>
        <DialogTitle>Reset to Step Up default?</DialogTitle>
        <DialogContent>
          <Typography>
            This will overwrite <strong>{resetting?.name}</strong> with the copy shipped by Step Up.
            Any admin customisations to this template will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetting(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmReset} disabled={resetState.isLoading}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={toast?.severity || "info"} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageTemplatesPage;
